import type { Tutorial } from '../../shared/types.js';

export const githubPrFlowTutorial: Tutorial = {
  name: 'github-pr-flow',
  title: 'Build an automation that responds to GitHub PRs',
  description:
    'Use the GitHub App webhooks to drive an automation that runs automatically whenever a pull request is opened. This will review the change with an agent and add comments. Recommended to do right after installing the GitHub App.',
  category: 'flows',
  docPages: ['concepts/flows'],
  agenda: [
    'Start an automation from a GitHub event',
    'Describe it and pick a model',
    'Watch Reflex build the graph',
    'Confirm the trigger and publish',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'React to GitHub events',
      body: "The GitHub App you just installed delivers repo events over webhooks. Let's turn those into action: an automation that fires whenever a pull request is opened, reviews the change with an agent, and comments back. You'll describe it in plain English and let Reflex build the graph.",
      path: '/automations',
    },
    {
      id: 'new-flow',
      kind: 'action',
      trigger: 'click',
      title: 'Start a new automation',
      body: 'Click "New Automation" to open the automation builder.',
      target: '[data-tutorial-id="flows-new"]',
      placement: 'bottom',
    },
    {
      id: 'describe',
      kind: 'action',
      trigger: 'input',
      title: 'Describe the automation',
      body: 'In the **Generate flow with AI** box, describe an automation that reacts to a GitHub event: the repo, the trigger (a pull request opening), and what the agent should do. Use **Fill example** to start from a sample, then swap in any repo the GitHub App can access.',
      example:
        'When a pull request is opened in owner/repo, run an agent task that reviews the changes for bugs and security issues, then posts a summary comment on the pull request.',
      target: '[data-tutorial-id="flow-generate-prompt"]',
      placement: 'bottom',
      path: '/automations/new',
    },
    {
      id: 'model-key',
      kind: 'info',
      title: 'Pick a model and key',
      body: 'Choose the model that should build - and later run - the automation, plus the provider key it authenticates with. If your organization has no key yet, you can add one right here without leaving the page.',
      target: '[data-tutorial-id="flow-generate-model"]',
      placement: 'bottom',
    },
    {
      id: 'generate',
      kind: 'action',
      trigger: 'click',
      title: 'Generate the automation',
      body: 'Click the **Generate automation** arrow button. Reflex opens the editor and builds the graph live: a GitHub Pull Request trigger, an agent task that reviews the PR, and a step that comments back.',
      target: '[data-tutorial-id="flow-generate-submit"]',
      placement: 'bottom',
    },
    {
      id: 'watch-build',
      kind: 'info',
      title: 'Watch Reflex build it',
      body: "Reflex reads your description and assembles the automation right here in the chat. Watch it stream each step it adds. You'll see it pick the GitHub Pull Request trigger, write the review task, and wire up the comment step as it goes.",
      target: '[data-testid="flow-editor-chat"]',
      placement: 'left',
    },
    {
      id: 'watch-graph',
      kind: 'info',
      title: 'The graph fills in live',
      body: 'New nodes appear on the canvas as Reflex adds them: a GitHub Pull Request trigger, an agent task that reviews the change, and a step that comments back. This is the graph that will run on every matching PR.',
      target: '[data-tutorial-id="flow-editor-canvas"]',
      placement: 'right',
    },
    {
      id: 'review-publish',
      kind: 'info',
      // No dim/lock: the build is done. The trigger config matters most here -
      // the user should select it on the canvas and confirm the repo + which PR
      // actions fire it - so hand the page back rather than boxing them in.
      dim: false,
      title: 'Confirm the trigger, then publish',
      body: 'Take your time; the page is yours. Select the trigger on the canvas and confirm it\'s a **GitHub Pull Request** trigger pointed at your repo and the PR actions you care about (opened, synchronized, …). Open the agent task to check the repo and prompt. When it looks right, click "Publish" - from then on the automation runs automatically whenever a matching PR event arrives over the App\'s webhooks.',
    },
  ],
  // The prerequisite that makes this flow possible: the GitHub App (installed
  // via connect-github) is what delivers the PR webhooks this flow reacts to.
  related: [{ tutorialName: 'connect-github', label: 'Install the GitHub App for webhooks' }],
};
